import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { checkoutAPI } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, ArrowRight } from "lucide-react";

const POLL_MS = 2000;
const MAX_POLLS = 20;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const kind = params.get("kind") || "";
  const [state, setState] = useState({ status: "polling", tries: 0, error: "" });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", tries: 0, error: "Missing session id" });
      return;
    }
    let alive = true;
    let tries = 0;
    const tick = async () => {
      try {
        const r = await checkoutAPI.getStatus(sessionId);
        if (!alive) return;
        if (r.data?.payment_status === "paid") {
          setState({ status: "paid", tries, error: "" });
          return;
        }
        tries += 1;
        if (tries >= MAX_POLLS) {
          setState({ status: "timeout", tries, error: "Payment not confirmed yet" });
          return;
        }
        setState({ status: "polling", tries, error: "" });
        setTimeout(tick, POLL_MS);
      } catch (e) {
        if (!alive) return;
        setState({ status: "error", tries, error: e.response?.data?.detail || "Could not verify payment" });
      }
    };
    tick();
    return () => { alive = false; };
  }, [sessionId]);

  const returnHref =
    kind === "supplies" ? "/my-listings"
    : kind === "purchase" ? "/orders"
    : "/";
  const returnLabel =
    kind === "supplies" ? "Back to my listings"
    : kind === "purchase" ? "View my orders"
    : "Continue";

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center p-6">
      <Card className="w-full border-border bg-card p-8 text-center" data-testid="payment-success">
        {state.status === "polling" && (
          <>
            <Loader2 className="mx-auto mb-3 h-10 w-10 animate-spin text-primary" />
            <h1 className="font-display text-2xl font-bold uppercase">Verifying payment…</h1>
            <p className="mt-2 text-sm text-muted-foreground">Give us a second while Stripe confirms this transaction.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-400" />
            <h1 className="font-display text-2xl font-bold uppercase">Payment received</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {kind === "supplies"
                ? "Your supplies order is paid. We'll ship packing materials to your address."
                : kind === "purchase"
                ? "Your purchase is confirmed. The seller has been notified to arrange shipping."
                : "Thanks for your payment."}
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Link to={returnHref}>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="payment-success-continue">
                  {returnLabel} <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </>
        )}
        {(state.status === "timeout" || state.status === "error") && (
          <>
            <XCircle className="mx-auto mb-3 h-12 w-12 text-amber-400" />
            <h1 className="font-display text-2xl font-bold uppercase">Not confirmed yet</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {state.error || "Stripe hasn't confirmed the payment yet. It usually clears within a minute."}
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Button variant="outline" onClick={() => window.location.reload()}>Re-check</Button>
              <Button variant="outline" onClick={() => navigate(returnHref)}>{returnLabel}</Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const kind = params.get("kind") || "";
  const backHref = kind === "supplies" ? "/my-listings" : kind === "purchase" ? "/messages" : "/";
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center p-6">
      <Card className="w-full border-border bg-card p-8 text-center" data-testid="payment-cancel">
        <XCircle className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
        <h1 className="font-display text-2xl font-bold uppercase">Payment canceled</h1>
        <p className="mt-2 text-sm text-muted-foreground">Nothing was charged. You can try again anytime.</p>
        <div className="mt-6 flex justify-center gap-3">
          <Link to={backHref}>
            <Button variant="outline">Go back</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
